import API from 'constants/API';

import {useCallback} from 'react';

import {Blob, DirectUpload} from 'activestorage';
import {useSetGlobalState} from 'hooks/useSetGlobalState';
import {useDropzone} from 'react-dropzone';
import {setErrorAC} from 'store/app/appSlice';

const allowedFileTypes = ['jpg', 'jpeg', 'jpe', 'png', 'gif'];

interface IProps {
  route?: string;
  children: React.ReactNode;
  multiple?: boolean;
  setImageName?: (imageName: string) => void;
  callback?: (blob: Blob, files: File[], allowedFileTypes: string[]) => void;
}

const ImageUploader = ({
  children,
  multiple = false,
  setImageName = () => {},
  callback = () => {},
  route = API.ACTIVE_STORAGE,
}: IProps) => {
  const dispatch = useSetGlobalState();

  const createBlob = (files: File[]) => {
    const file = files[0];

    const fileType = file?.name?.split('.')?.pop()?.toLowerCase();
    setImageName(file?.name);

    if (allowedFileTypes.includes(fileType!)) {
      const upload = new DirectUpload(file, route);

      upload.create((error: Error, blob: Blob) => {
        if (error) {
          dispatch(setErrorAC('There was an issue with selecting your image.'));
          return;
        }

        callback(blob, files, allowedFileTypes);
      });
    } else {
      const fileName = file?.name;
      const errorText = `The specified file
          ${fileName}
           could not be uploaded. The image is invalid or not supported.
         Allowed types: ${allowedFileTypes.map((type) => ` ${type}`)}.`;

      dispatch(setErrorAC(errorText));
    }
  };

  const onDrop = useCallback((acceptedFiles: any) => {
    createBlob(acceptedFiles);
    // eslint-disable-next-line
  }, []);

  const {getRootProps, getInputProps} = useDropzone({
    onDrop,
    multiple,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.jpe'],
    },
  });

  return (
    <div className="image-uploader" {...getRootProps()} data-testid="image-uploader">
      <input {...getInputProps()} />
      {children}
    </div>
  );
};

export default ImageUploader;
